import React, {useState} from "react";
import {api} from "../../config/api";
import secureStorage from "../../utils/secureStorage";
import {useQueryClient} from '@tanstack/react-query';
import {Modal, ModalFooter, FormField, SwitchRow, inputClass} from "./SettingsUI";

const ClientTypeEditor = ({clientType, onClose, showNotification, onSaved}) => {
    const [formData, setFormData] = useState({
        name: clientType ? clientType.name : '',
        active: clientType ? clientType.active : true
    });
    const [submitting, setSubmitting] = useState(false);
    const [errors, setErrors] = useState({});

    const queryClient = useQueryClient();

    const handleChange = (e) => {
        const {name, value, type, checked} = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));

        // Clear error for this field when user starts typing
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: null
            }));
        }
    };

    const validate = () => {
        const newErrors = {};

        if (!formData.name.trim()) {
            newErrors.name = "Le nom du type de client est requis";
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validate()) {
            return;
        }

        setSubmitting(true);

        try {
            const accountId = await secureStorage.getItem("selectedAccount");

            // Prepare request data
            const payload = {
                name: formData.name,
                active: formData.active
            };

            let response;
            if (clientType) {
                // Update existing client type
                response = await api.put(`/client-types/${clientType.id}`, payload, {
                    headers: {
                        'X-account-id': accountId.id,
                        'Accept': 'application/json',
                        'Content-Type': 'application/json'
                    }
                });
                showNotification('success', 'Type de client mis à jour avec succès');
            } else {
                // Create new client type
                response = await api.post('/client-types', payload, {
                    headers: {
                        'X-account-id': accountId.id,
                        'Accept': 'application/json',
                        'Content-Type': 'application/json'
                    }
                });
                showNotification('success', 'Type de client créé avec succès');
            }

            // Invalidate queries to refetch data
            queryClient.invalidateQueries({queryKey: ['client-types']});
            onSaved?.(response.data);
            onClose();
        } catch (error) {
            const errorMessage = error.response?.data?.detail ||
                error.response?.data?.message ||
                "Une erreur s'est produite";
            // A duplicate name for the account is a validation error tied to the
            // name field, not a generic failure — surface it inline like the others.
            if (error.response?.status === 409 || /existe|duplic|unique/i.test(errorMessage)) {
                setErrors(prev => ({...prev, name: errorMessage}));
            } else {
                showNotification('error', `Erreur: ${errorMessage}`);
            }
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Modal
            title={clientType ? 'Modifier le type de client' : 'Nouveau type de client'}
            onClose={onClose}
            footer={<ModalFooter onCancel={onClose} form="client-type-form" saving={submitting}
                                 label={clientType ? 'Enregistrer' : 'Créer'}/>}
        >
            <form id="client-type-form" onSubmit={handleSubmit} className="space-y-4">
                <FormField id="name" label="Nom du type de client" required error={errors.name}
                           hint="La remise associée à ce type se configure dans Catalogue > Remises.">
                    <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        className={inputClass(errors.name)}
                        placeholder="ex: VIP"
                    />
                </FormField>

                <SwitchRow id="active" label="Actif" checked={formData.active}
                           onChange={(checked) => setFormData(prev => ({...prev, active: checked}))}/>
            </form>
        </Modal>
    );
};

export default ClientTypeEditor;
